import {
  type LucideIcon,
  BriefcaseBusiness,
  CalendarClock,
  Globe2,
  Headphones,
  LifeBuoy,
  Mail,
  MessageCircle,
  ShieldCheck
} from 'lucide-react';
import { Button, Card, Hero, Input, Section } from '../components/ui';

type ContactMethod = {
  title: string;
  description: string;
  email: string;
  icon: LucideIcon;
  response: string;
};

const contactMethods: ContactMethod[] = [
  {
    title: 'Sales',
    description: 'Interested in textadapt for your organization?',
    email: 'sales@textadapt.com',
    icon: BriefcaseBusiness,
    response: 'Replies within one business day'
  },
  {
    title: 'Support',
    description: 'Need help with your account?',
    email: 'support@textadapt.com',
    icon: LifeBuoy,
    response: '24/7 coverage for all plans'
  },
  {
    title: 'Security',
    description: 'Security inquiries and compliance questions',
    email: 'security@textadapt.com',
    icon: ShieldCheck,
    response: 'Security reviews within 2 business days'
  },
  {
    title: 'General',
    description: 'All other inquiries',
    email: 'hello@textadapt.com',
    icon: Mail,
    response: 'Same-day response during business hours'
  }
];

type ContactHighlight = {
  label: string;
  value: string;
  icon: LucideIcon;
};

const contactHighlights: ContactHighlight[] = [
  {
    label: 'Average response time',
    value: '< 1 hour',
    icon: CalendarClock
  },
  {
    label: 'Global coverage',
    value: 'US • EU • APAC',
    icon: Globe2
  },
  {
    label: 'Support availability',
    value: '24/7 priority',
    icon: Headphones
  }
];

export const Contact = () => {
  return (
    <>
      <Hero
        title="Get in Touch"
        description="Have questions about textadapt? We're here to help you get started with automated document intelligence."
        size="sm"
      />

      <Section
        title="Talk with our team"
        subtitle="Tell us about your document workflows and we’ll respond within one business day."
        centered
      >
        <div className="contact-layout">
          <Card elevation="lg" className="contact-panel contact-panel--form">
            <form className="contact-form">
              <div className="contact-form__row">
                <Input
                  label="First Name"
                  type="text"
                  placeholder="John"
                  required
                />
                <Input
                  label="Last Name"
                  type="text"
                  placeholder="Doe"
                  required
                />
              </div>

              <Input
                label="Work Email"
                type="email"
                placeholder="name@company.com"
                required
              />

              <Input
                label="Company"
                type="text"
                placeholder="Your Company"
              />

              <div className="contact-form__field">
                <label className="contact-form__label">
                  Inquiry Type <span aria-hidden className="contact-form__required">*</span>
                </label>
                <select
                  required
                  className="contact-select"
                  defaultValue=""
                >
                  <option value="" disabled>
                    Select an option
                  </option>
                  <option value="sales">Sales & Demo Request</option>
                  <option value="support">Technical Support</option>
                  <option value="security">Security & Compliance</option>
                  <option value="partnership">Partnership Opportunities</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="contact-form__field">
                <label className="contact-form__label">
                  Message <span aria-hidden className="contact-form__required">*</span>
                </label>
                <textarea
                  required
                  rows={6}
                  placeholder="Share your current process, volumes, or requirements..."
                  className="contact-textarea"
                />
              </div>

              <Button type="submit" variant="primary" size="lg" fullWidth className="contact-form__submit">
                Send Message
              </Button>

              <p className="contact-form__disclaimer">
                By submitting this form, you agree to our Privacy Policy
              </p>
            </form>
          </Card>

          <Card elevation="lg" className="contact-panel contact-panel--info">
            <div className="contact-panel__header">
              <h3>Work directly with a product specialist</h3>
              <p>
                We’ll tailor a demo, security documentation, and rollout plan based on your volume, vendor landscape,
                and systems.
              </p>
            </div>

            <div className="contact-highlights">
              {contactHighlights.map((highlight) => (
                <div key={highlight.label} className="contact-highlight">
                  <span className="contact-highlight__icon" aria-hidden>
                    <highlight.icon size={20} strokeWidth={1.75} />
                  </span>
                  <div className="contact-highlight__content">
                    <span className="contact-highlight__label">{highlight.label}</span>
                    <span className="contact-highlight__value">{highlight.value}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="contact-panel__meta">
              <div>
                <ShieldCheck size={18} strokeWidth={1.75} aria-hidden />
                SOC 2 Type II controls & custom security reviews on request
              </div>
              <div>
                <MessageCircle size={18} strokeWidth={1.75} aria-hidden />
                Dedicated onboarding specialists for finance and operations teams
              </div>
            </div>

            <div className="contact-panel__footer">
              <p>Prefer a direct introduction? Our sales team will coordinate a time that works best for you.</p>
              <a href="mailto:sales@textadapt.com">sales@textadapt.com</a>
            </div>
          </Card>
        </div>
      </Section>

      <Section
        title="Other Ways to Reach Us"
        subtitle="Skip the form and connect with the team best suited to help."
        background="subtle"
        centered
      >
        <div className="contact-methods">
          {contactMethods.map((method) => (
            <Card key={method.title} elevation="md" className="contact-method-card">
              <div className="contact-method-card__icon" aria-hidden>
                <method.icon size={32} strokeWidth={1.75} />
              </div>
              <h3>{method.title}</h3>
              <p>{method.description}</p>
              <span className="contact-method-card__meta">{method.response}</span>
              <a href={`mailto:${method.email}`} className="contact-method-card__link">
                {method.email}
              </a>
            </Card>
          ))}
        </div>
      </Section>

      <Section title="Office Locations" centered>
        <div className="contact-office-grid">
          <Card elevation="md" className="contact-office-card">
            <div className="contact-office-card__body">
              <div className="contact-office-card__address">
                <h3>textadapt Headquarters</h3>
                <p>123 Innovation Drive</p>
                <p>San Francisco, CA 94103</p>
                <p>United States</p>
              </div>

              <div className="contact-office-card__map">
                <span role="img" aria-label="Map icon">
                  📍
                </span>
                <span>Maps integration coming soon</span>
              </div>
            </div>
          </Card>

          <Card elevation="md" className="contact-office-card">
            <div className="contact-office-card__body">
              <div className="contact-office-card__address">
                <h3>Global Support Coverage</h3>
                <p>Distributed customer teams in North America, Europe, and APAC.</p>
                <p>Live chat and scheduled reviews across your local working hours.</p>
              </div>

              <div className="contact-office-card__map">
                <span role="img" aria-label="Globe icon">
                  🌍
                </span>
                <span>Follow-the-sun assistance across three time zones</span>
              </div>
            </div>
          </Card>
        </div>
      </Section>
    </>
  );
};
